/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
'use client';

import { Dialog, Switch } from '@headlessui/react';
import {
    mdiCheckBold,
    mdiCloseThick,
    mdiDelete,
    mdiTuneVariant,
} from '@mdi/js';
import Icon from '@mdi/react';
import { useEffect, useState } from 'react';
import useLocalStorage from '@/hooks/useLocalStorage';
import localFont from 'next/font/local';
import { Subject } from '@/hooks/useSubject';
import IconButton from '@/components/Button/IconButton';
import { slugify } from '@/utils/strings';
import { useSubjectProgress } from '@/hooks/useSubjectProgress';
import { useReducedMotion, motion } from 'framer-motion';
import EmojiText from '../EmojiText/EmojiText';
import { useStrings } from '@/resources/translations/translations';
import SingleBlock from '../Block/SingleBlock';
import { measure } from '../Analytics/Analytics';

const openDyslexic = localFont({
    src: [
        {
            path: './fonts/OpenDyslexic-Regular.woff2',
            weight: '400',
            style: 'normal',
        },
        {
            path: './fonts/OpenDyslexic-Bold.woff2',
            weight: '700',
            style: 'normal',
        },
        {
            path: './fonts/OpenDyslexic-Italic.woff2',
            weight: '400',
            style: 'italic',
        },
    ],
});

const enableDyslexiaFont = (enabled: boolean) => {
    if (enabled) {
        document.body.classList.add(openDyslexic.className);
    } else {
        document.body.classList.remove(openDyslexic.className);
    }
};

const AccessibilityMenu = ({
    subject,
    buttonClassName = '',
    buttonIcon = mdiTuneVariant,
}: {
    subject: Subject;
    buttonClassName?: string;
    buttonIcon?: string;
}) => {
    const strings = useStrings('components.AccessibilityMenu');
    const reduceMotion = useReducedMotion();

    const { resetSubjectProgress } = useSubjectProgress(subject.id);

    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [hasRenderedClientSide, setHasRenderedClientSide] = useState(false);

    const [
        isStaticChatTimelineEnabled,
        setIsStaticChatTimelineEnabled,
    ] = useLocalStorage('isStaticChatTimelineEnabled', false);

    const [
        isScreenReaderAnnouncementsEnabled,
        setIsScreenReaderAnnouncementsEnabled,
    ] = useLocalStorage('isScreenReaderAnnouncementsEnabled', true);

    const [isDyslexiaFontEnabled] = useLocalStorage(
        'isDyslexiaFontEnabled',
        false,
    );

    useEffect(() => {
        setHasRenderedClientSide(true);
        enableDyslexiaFont(isDyslexiaFontEnabled);
    }, []);

    if (!hasRenderedClientSide) return null;

    return (
        <>
            <IconButton
                className={buttonClassName}
                title={strings('open_button')}
                icon={buttonIcon}
                onClick={() => {
                    setIsMenuOpen(true);
                    measure('accessibility_menu_open');
                }}
            />
            <Dialog
                open={isMenuOpen}
                onClose={() => {
                    setIsMenuOpen(false);
                    measure('accessibility_menu_close', 'exit');
                }}
                className="fixed inset-0 z-200 overflow-y-auto font-bold"
            >
                <Dialog.Overlay className="fixed inset-0 bg-secondary/20 filter backdrop-blur-lg z-200" />
                <Dialog.Panel className="fixed inset-0 w-full h-full overflow-y-auto z-200">
                    <div className="grid items-center justify-stretch min-h-screen px-4 py-12">
                        <motion.div
                            className="row-start-1 row-end-1 col-start-1 col-end-1 max-w-sm w-full mx-auto bg-white rounded-xl shadow-2xl overflow-hidden"
                            initial={{
                                opacity: 0,
                                scale: reduceMotion ? 1 : 0.8,
                            }}
                            animate={{ opacity: 1, scale: 1, x: 0 }}
                        >
                            <SettingsSection
                                title={strings('section_general')}
                                hasCloseButton
                                close={() => setIsMenuOpen(false)}
                            >
                                <Dialog.Title
                                    aria-hidden="true"
                                    className="sm:col-span-2 flex items-center justify-center p-4 bg-keppel/30 rounded-lg"
                                >
                                    <SingleBlock
                                        data={{
                                            text: strings(
                                                'preview_chat_message',
                                            ),
                                        }}
                                        type="chat_text"
                                        isAnimated={false}
                                    />
                                </Dialog.Title>
                                <EmbeddableTextAccessibilitySettings />
                                <SettingSwitch
                                    analyticsId="static_chat_timeline"
                                    checked={isStaticChatTimelineEnabled}
                                    onChange={(checked) => {
                                        setIsStaticChatTimelineEnabled(checked);
                                    }}
                                    label={strings(
                                        'option_static_chat_timeline',
                                    )}
                                    emoji="📜"
                                />
                            </SettingsSection>

                            <SettingsSection
                                title={strings('section_screenreaders')}
                            >
                                <SettingSwitch
                                    analyticsId="screenreader_announcements"
                                    checked={isScreenReaderAnnouncementsEnabled}
                                    onChange={(checked) => {
                                        setIsScreenReaderAnnouncementsEnabled(
                                            checked,
                                        );
                                    }}
                                    label={
                                        <>
                                            {strings(
                                                'option_screenreader_announcements',
                                            )}
                                            <span className="sr-only">
                                                {strings(
                                                    'option_screenreader_announcements_info',
                                                )}
                                            </span>
                                        </>
                                    }
                                    emoji="📢"
                                />
                            </SettingsSection>

                            <SettingsSection title={subject.title}>
                                <SettingsButton
                                    analyticsId="story_restart"
                                    label={strings('option_restart')}
                                    title={strings('option_restart_title')}
                                    icon={mdiDelete}
                                    onClick={() => {
                                        if (
                                            confirm(
                                                strings(
                                                    'option_restart_confirmation',
                                                    {
                                                        subject: subject.title,
                                                    },
                                                ),
                                            )
                                        ) {
                                            resetSubjectProgress();
                                            window.location.href = `/story/${
                                                subject.id
                                            }/${slugify(subject.title)}`;
                                        }
                                    }}
                                />
                            </SettingsSection>
                        </motion.div>
                    </div>
                </Dialog.Panel>
            </Dialog>
        </>
    );
};

const SettingsSection = ({
    title,
    children,
    hasCloseButton = false,
    close,
}: {
    title: string;
    children: React.ReactNode;
    hasCloseButton?: boolean;
    close?: () => void;
}) => {
    return (
        <figure>
            <figcaption>
                <h2
                    className={`flex items-center justify-between bg-secondary-text-base-aaa pl-4 p-2 text-xl text-white shadow-md ${
                        hasCloseButton ? 'md:rounded-tl-lg' : ''
                    }`}
                >
                    <span className="text-lg py-2">{title}</span>
                    {hasCloseButton && (
                        <IconButton
                            icon={mdiCloseThick}
                            title="Opties sluiten"
                            className="bg-white text-secondary-text-base-aaa"
                            onClick={() => close?.()}
                        />
                    )}
                </h2>
            </figcaption>
            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2 p-2">
                {children}
            </ul>
        </figure>
    );
};

const SettingSwitch = ({
    emoji,
    label,
    checked,
    analyticsId,
    onChange,
}: {
    emoji: string;
    label: string | React.ReactNode;
    checked: boolean;
    analyticsId: string;
    onChange: (checked: boolean) => void;
}) => {
    const internalOnChange = (checked: boolean) => {
        measure(`${analyticsId}_${checked ? 'on' : 'off'}`);
        onChange(checked);
    };

    return (
        <li className="bg-secondary/10 rounded-lg">
            <Switch.Group>
                <div
                    className="relative group cursor-pointer p-4 transition-all last:border-b-0 hover:bg-white/10 min-h-[8.125rem]"
                    onClick={() => internalOnChange(!checked)}
                >
                    <Switch.Label className="grow origin-left cursor-pointer text-balance text-base font-bold select-none transition-all motion-safe:group-active:scale-95">
                        <div className="text-4xl pb-1" aria-hidden>
                            <EmojiText>{emoji}</EmojiText>
                        </div>
                        {label}
                    </Switch.Label>
                    <Switch
                        checked={checked}
                        onChange={internalOnChange}
                        onClick={(event) => {
                            // Block the click event from propagating to the parent
                            event.stopPropagation();
                        }}
                        className={`${
                            checked ? 'bg-secondary-text-lg-aa' : 'bg-white'
                        } absolute top-4 right-4 shrink-0 inline-flex h-6 w-10 origin-right items-center rounded-full transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-secondary-text-base-aa focus-visible:ring-offset-2 motion-safe:group-active:scale-90`}
                    >
                        <span
                            className={`${
                                checked
                                    ? 'translate-x-[1.125rem] bg-white'
                                    : 'translate-x-0.5 bg-secondary-text-lg-aa'
                            } inline-flex items-center justify-center h-5 w-5 rounded-full transition-transform`}
                        >
                            <Icon
                                path={checked ? mdiCheckBold : mdiCloseThick}
                                size={0.7}
                                className={`${
                                    checked
                                        ? 'text-secondary-text-lg-aa'
                                        : 'text-white'
                                }`}
                            />
                        </span>
                    </Switch>
                </div>
            </Switch.Group>
        </li>
    );
};

const SettingsButton = ({
    label,
    title,
    icon,
    analyticsId,
    onClick,
}: {
    label: string;
    title: string;
    icon: string;
    analyticsId: string;
    onClick: () => void;
}) => {
    const internalOnClick = () => {
        measure(analyticsId);
        onClick();
    };

    return (
        <li className="bg-secondary/10 rounded-lg">
            <button
                className="group flex items-center justify-between w-full border-b-2 border-white/25 text-base transition-all hover:bg-white/10 text-left"
                onClick={internalOnClick}
                title={title}
            >
                <span className="flex items-center gap-3 p-4 transition-all motion-safe:active:scale-95">
                    <Icon className="shrink-0" path={icon} size={1.25} />
                    {label}
                </span>
            </button>
        </li>
    );
};

export const EmbeddableTextAccessibilitySettings = () => {
    const strings = useStrings('components.AccessibilityMenu');

    const [isDyslexiaFontEnabled, setIsDyslexiaFontEnabled] = useLocalStorage(
        'isDyslexiaFontEnabled',
        false,
    );

    useEffect(() => {
        enableDyslexiaFont(isDyslexiaFontEnabled);
    }, [isDyslexiaFontEnabled]);

    useEffect(() => {
        enableDyslexiaFont(isDyslexiaFontEnabled);
    }, []);

    const [
        isIncreasedTextSizeEnabled,
        setIsIncreasedTextSizeEnabled,
    ] = useLocalStorage('isIncreasedTextSizeEnabled', false);

    return (
        <>
            <SettingSwitch
                analyticsId="dyslexia_font"
                checked={isDyslexiaFontEnabled}
                onChange={(checked) => {
                    setIsDyslexiaFontEnabled(checked);
                }}
                label={strings('option_dyslexia_font')}
                emoji="👀"
            />
            <SettingSwitch
                analyticsId="increased_text_size"
                checked={isIncreasedTextSizeEnabled}
                onChange={(checked) => {
                    setIsIncreasedTextSizeEnabled(checked);
                }}
                label={strings('option_increased_text_size')}
                emoji="🐘"
            />
        </>
    );
};

export default AccessibilityMenu;
